import { useUI } from '@/store/ui'
import { ApiError, DiwaAPI, UnauthorizedError } from './client'

/** Mirrors /domains in products/diwa/src/diwa/domains/router.py */

export type EgoRelationKind = 'event' | 'state'

/** A Me → entity edge spec. Template forks may carry extras (weight, group) beyond `kind`. */
export interface EgoRelationSpec {
  kind: EgoRelationKind
  [extra: string]: unknown
}

export interface Domain {
  id: string
  name: string
  slug: string
  description: string
  type: string
  status: string
  entity_types: string[]
  relation_types: string[]
  /** Optional so code-defined domains (e.g. the built-in CRM object) stay valid. */
  ego_relations?: Record<string, EgoRelationSpec>
  /** Which types catch signals by name — empty means the matcher never routes here. */
  routable_types?: string[]
  attached_springs?: string[]
  /** User-authored labels — ≤12 lowercase slugs; [] on domains that predate tags. */
  tags?: string[]
  /** Membership marking — a joined region is never disguised as your own. */
  shared?: boolean
  role?: string | null
  owner?: string | null
  published?: boolean
}

/** GET /domains/{id} — the domain plus live entity/relation counts. */
export interface DomainDetail extends Domain {
  counts: { entities: number; relations: number }
}

export interface DomainTemplate {
  key: string
  name: string
  description: string
}

export interface DomainsList {
  domains: Domain[]
  templates: DomainTemplate[]
}

/** A liveness state as classified server-side from lore health — mirrors StatusDotState. */
export type SpringLiveness = 'live' | 'idle' | 'error' | 'neutral'

/** One attachable spring in the builder catalog, stamped with liveness (GET /domains/catalog). */
export interface CatalogSpring {
  name: string
  tags: string[]
  /** Source class — display only; the tier gate stays server-side. */
  class: string
  liveness: SpringLiveness
  last_run: string | null
}

/** A suggested Me → entity relation for a category, with plain words. */
export interface CategoryEgoSuggestion {
  name: string
  kind: EgoRelationKind
  gloss: string
  /** state kinds are exclusive within a group; carried through so forks keep semantics. */
  group?: string
}

/** A curated system category — the reliable floor the builder rail suggests from. */
export interface DomainCategory {
  key: string
  label: string
  entity_types: string[]
  edge_types: string[]
  ego_relations: CategoryEgoSuggestion[]
  springs: CatalogSpring[]
}

export interface DomainCatalog {
  categories: DomainCategory[]
}

/** One template previewed honestly — what you'd GET, before you commit. */
export interface TemplatePreview {
  key: string
  name: string
  description: string
  entity_types: string[]
  relation_types: string[]
  springs: string[]
  /** Plain words per entity type — full coverage, never a blank. */
  words: Record<string, string>
  /** Me → entity edges in plain words — what the template records about YOU. */
  ego_relations: { name: string; kind: EgoRelationKind; word: string }[]
  has_default_rules: boolean
}

/** Fields a user can set on a domain — POST body (plus `template`) and PATCH body. */
export interface DomainDraft {
  name?: string
  description?: string
  status?: 'active' | 'archived'
  entity_types?: string[]
  relation_types?: string[]
  ego_relations?: Record<string, EgoRelationSpec>
  /** Plain-language words per entity type — the vocabulary tooltips, user-authored. */
  words?: Record<string, string>
  /** Which types catch signals by name once a spring feeds the region. */
  routable_types?: string[]
  attached_springs?: string[]
  /** User-authored labels — PATCH replaces the whole list ([] clears it). */
  tags?: string[]
}

/** POST /domains 409 — the slug already exists. `detail` is the backend's message, shown inline. */
export class DomainConflictError extends Error {
  constructor(public readonly detail: string) {
    super(detail)
  }
}

/** Mirrors the backend's slugify(): lowercase, non-alphanumeric runs → '-', trimmed, capped at 64. */
export function domainSlug(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64) || 'domain'
  )
}

export class DomainsAPI {
  private api: DiwaAPI

  constructor(private baseUrl: string) {
    this.api = new DiwaAPI(baseUrl)
  }

  list(): Promise<DomainsList> {
    return this.api.get<DomainsList>('/domains')
  }

  /** The builder rail's catalog: categories → suggested vocabulary + attachable springs. */
  catalog(): Promise<DomainCatalog> {
    return this.api.get<DomainCatalog>('/domains/catalog')
  }

  /** The template gallery's truth: entity/edge/ego vocabulary in plain words. */
  templatePreviews(): Promise<{ templates: TemplatePreview[] }> {
    return this.api.get<{ templates: TemplatePreview[] }>('/domains/templates/preview')
  }

  detail(id: string): Promise<DomainDetail> {
    return this.api.get<DomainDetail>(`/domains/${id}`)
  }

  /**
   * Raw fetch rather than the shared client: DiwaAPI discards error bodies, and a 409's
   * {"detail": ...} is exactly the message we surface under the name field.
   */
  async create(body: DomainDraft & { template?: string }): Promise<Domain> {
    const token = useUI.getState().token
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(`${this.baseUrl}/domains`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    })
    if (res.status === 401) throw new UnauthorizedError()
    if (res.status === 409) {
      const data = (await res.json().catch(() => null)) as { detail?: string } | null
      throw new DomainConflictError(data?.detail ?? 'A domain with that name already exists.')
    }
    if (!res.ok) throw new ApiError(res.status, `POST /domains failed: ${res.status}`)
    return res.json()
  }

  update(id: string, body: DomainDraft): Promise<Domain> {
    return this.api.patch<Domain>(`/domains/${id}`, body)
  }

  remove(id: string): Promise<{ deleted: boolean }> {
    return this.api.del<{ deleted: boolean }>(`/domains/${id}`)
  }
}
